// §6.3 / §5.5 / §6.4 — Task View, and the Alt-Tab strip that shares its machinery.
//
//   • `overview` is Task View: a grid of window thumbnails over a row of desktops, opened by
//     `Meta+Tab` and left open.
//   • `switcher` is Alt-Tab: open only while its modifier is held, advanced by pressing the chord
//     again, committed the moment that modifier comes back up.
//
// Cancelling costs nothing in either — nothing here focuses a window until it is committed, so
// `Esc` leaves the desktop exactly as it found it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { COPY } from '@/copy';
import { useKernel, useStoreValue, useWindows } from '@/kernel/react';
import { modifiersOf } from '@/kernel/keyboard';
import { isGridKey, moveInGrid } from '@/kernel/wm/grid';
import { sortDesktops } from '@/kernel/wm/desktops';

/** Task View lays thumbnails out four across; arrow-up and arrow-down move by this much. */
export const TASK_VIEW_COLUMNS = 4;

export function Switcher() {
  const kernel = useKernel();
  const overlay = useStoreValue(kernel.shellui.store, (s) => s.overlay);
  const open = overlay === 'switcher' || overlay === 'overview';
  const cycling = overlay === 'switcher';
  const allWindows = useWindows();
  const [selected, setSelected] = useState(0);
  const surface = useRef<HTMLDivElement | null>(null);

  // Subscribed, not read once: switching desktops from the strip re-filters the grid.
  const activeDesktop = useStoreValue(kernel.wm.store, (s) => s.activeDesktopId);
  const desktopList = useStoreValue(kernel.wm.store, (s) => s.desktops);
  const desktops = useMemo(() => sortDesktops(desktopList), [desktopList]);

  // Most recently focused first: the window you want is nearly always the one you just left.
  const windows = useMemo(
    () =>
      allWindows
        .filter((win) => win.desktopId === activeDesktop)
        .slice()
        .sort((a, b) => b.focusedAt - a.focusedAt),
    [allWindows, activeDesktop],
  );

  useEffect(() => {
    if (!open) return;
    setSelected(cycling ? 1 : 0);
    surface.current?.focus();
  }, [open, cycling]);

  const commit = useCallback(
    (index: number) => {
      const win = windows[index];
      kernel.shellui.closeOverlay();
      if (!win) return;
      if (win.state === 'minimized') kernel.wm.restore(win.id);
      kernel.wm.focus(win.id);
    },
    [kernel, windows],
  );

  const pending = useRef({ commit, selected });
  pending.current = { commit, selected };

  // §6.4 — advance on every further press of the chord that opened it.
  useEffect(() => {
    if (!cycling) return;
    return kernel.bus.on('shell:switcherAdvance', ({ delta }) => {
      setSelected((index) =>
        moveInGrid(
          index,
          windows.length,
          TASK_VIEW_COLUMNS,
          delta > 0 ? 'ArrowRight' : 'ArrowLeft',
        ),
      );
    });
  }, [cycling, kernel, windows.length]);

  // Which modifier is held comes from the *keymap*, not from a hard-coded Alt (D-091).
  useEffect(() => {
    if (!cycling) return;
    const held = modifiersOf(kernel.keyboard.chordFor('switcher.windows') ?? '');
    if (held.length === 0) return;
    const onKeyUp = (event: KeyboardEvent): void => {
      if (!held.includes(event.key)) return;
      pending.current.commit(pending.current.selected);
    };
    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
  }, [cycling, kernel]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const key = event.key;
      if (isGridKey(key)) {
        event.preventDefault();
        setSelected((index) => moveInGrid(index, windows.length, TASK_VIEW_COLUMNS, key));
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        commit(selected);
      }
      // Escape is the shell's: `Desktop` closes whatever overlay is open, wherever focus is.
    },
    [commit, selected, windows.length],
  );

  if (!open) return null;

  return (
    <div
      className="win11-taskview"
      data-testid="switcher"
      data-mode={cycling ? 'cycle' : 'overview'}
      role="dialog"
      aria-label={COPY.chrome.taskView}
      tabIndex={-1}
      ref={surface}
      onKeyDown={onKeyDown}
      onClick={() => kernel.shellui.closeOverlay()}
    >
      <div
        className="win11-taskview-grid"
        role="listbox"
        aria-label={COPY.chrome.openWindows}
        data-testid="switcher-grid"
        onClick={(event) => event.stopPropagation()}
      >
        {windows.map((win, index) => (
          <button
            key={win.id}
            type="button"
            role="option"
            aria-selected={index === selected}
            className="win11-taskview-tile"
            data-testid={`switcher-tile-${win.id}`}
            data-selected={String(index === selected)}
            onPointerEnter={() => setSelected(index)}
            onClick={() => commit(index)}
          >
            <span className="win11-taskview-thumb" aria-hidden="true" />
            <span className="win11-taskview-caption">
              <span
                className="win11-taskview-icon"
                aria-hidden
                dangerouslySetInnerHTML={{ __html: kernel.windowIconSvg(win) }}
              />
              <span className="win11-taskview-label">{win.title}</span>
            </span>
          </button>
        ))}
        {windows.length === 0 ? (
          <p className="win11-taskview-empty" data-testid="switcher-empty">
            {COPY.chrome.noOpenWindows}
          </p>
        ) : null}
      </div>

      {/* §5.5 — the desktop strip. Switching from here is a class toggle, never a DOM move. */}
      <div
        className="win11-taskview-desktops"
        role="tablist"
        aria-label={COPY.chrome.desktops}
        data-testid="switcher-desktops"
        onClick={(event) => event.stopPropagation()}
      >
        {desktops.map((desktop) => (
          <button
            key={desktop.id}
            type="button"
            role="tab"
            aria-selected={desktop.id === activeDesktop}
            className="win11-taskview-desktop"
            data-testid={`switcher-desktop-${desktop.id}`}
            data-active={String(desktop.id === activeDesktop)}
            onClick={() => {
              kernel.desktops.setActive(desktop.id);
              setSelected(0);
            }}
          >
            <span className="win11-taskview-desktop-thumb" aria-hidden="true" />
            {desktop.name}
          </button>
        ))}
        <button
          type="button"
          className="win11-taskview-desktop win11-taskview-add"
          aria-label={COPY.chrome.newDesktop}
          data-testid="switcher-desktop-new"
          onClick={() => kernel.desktops.create()}
        >
          +
        </button>
      </div>
    </div>
  );
}
